import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { updatePost, fetchBlogBySlug, fetchBlogCategories } from '../store/blogSlice';
import { selectCurrentBlog, selectCurrentBlogCategories } from '../store/selectors';
import { blogAPI } from '../api/blogApi';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loading } from '@/components/ui/loading';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';
import { AutoExpandTextarea } from '../components/AutoExpandTextarea';
import { ImageUploadSection } from '../components/ImageUploadSection';
import type { PostImage } from '../components/ImageUploadSection';

export const CreatePostPage = () => {
  const { blogSlug } = useParams<{ blogSlug: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const blog = useAppSelector(selectCurrentBlog);
  const categories = useAppSelector(selectCurrentBlogCategories);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    categoryId: 'none', // 'none' thay vì empty string
  });
  const [images, setImages] = useState<PostImage[]>([]);
  const [draftPostId, setDraftPostId] = useState<number | null>(null);
  const [coverImageId, setCoverImageId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [draftReady, setDraftReady] = useState(false);
  const draftInitializationRef = useRef<number | null>(null);

  useEffect(() => {
    if (blogSlug) {
      dispatch(fetchBlogBySlug(blogSlug));
    }
  }, [blogSlug, dispatch]);

  useEffect(() => {
    const blogId = blog?.id;
    if (blogId) {
      dispatch(fetchBlogCategories({ blogId }));

      // Redux may replace the blog object while the page is mounted. The
      // editor must initialize one draft request per blog, not per object
      // identity, otherwise create-post can be called twice concurrently.
      if (draftInitializationRef.current === blogId) return;
      draftInitializationRef.current = blogId;

      let active = true;
      (async () => {
        try {
          const response = await blogAPI.admin.createDraftPost(blogId);
          const draft = response.data;
          if (active) {
            setFormData({
              title: draft.title === 'Bản nháp' ? '' : draft.title,
              content: draft.content,
              categoryId: draft.category_id?.toString() || 'none',
            });
            setImages(draft.images.map((image, index) => ({
              id: image.id,
              url: image.url,
              type: image.type,
              caption: image.caption,
              sort_order: image.sort_order ?? index,
              driveFileId: image.drive_file_id ?? undefined,
              drive_file_id: image.drive_file_id,
              drive_owner_id: image.drive_owner_id,
            })));
            setDraftPostId(draft.id);
            setCoverImageId(draft.cover_image_id ?? null);
          }
        } catch (error) {
          console.error('Failed to initialize draft post:', error);
          draftInitializationRef.current = null;
          if (active) toast.error('Không thể chuẩn bị bản nháp bài viết');
        } finally {
          if (active) setDraftReady(true);
        }
      })();
      return () => { active = false; };
    }
  }, [blog?.id, dispatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!blog) {
      toast.error('Không tìm thấy blog');
      return;
    }

    if (!formData.title.trim()) {
      toast.error('Vui lòng nhập tiêu đề bài viết');
      return;
    }

    if (!formData.content.trim()) {
      toast.error('Vui lòng nhập nội dung bài viết');
      return;
    }

    // Check if any images are still uploading
    const uploadingImages = images.filter(img => img.isUploading);
    if (uploadingImages.length > 0) {
      toast.error(`Vui lòng đợi ${uploadingImages.length} ảnh/video đang upload hoàn tất`);
      return;
    }

    setLoading(true);
    try {
      if (!draftPostId) {
        toast.error('Bản nháp chưa sẵn sàng, vui lòng thử lại');
        return;
      }

      // Save edits while keeping the post as a draft.
      const updated = await dispatch(updatePost({
        id: draftPostId,
        data: {
          category_id: formData.categoryId !== 'none' ? parseInt(formData.categoryId) : undefined,
          title: formData.title.trim(),
          content: formData.content.trim(),
        },
      })).unwrap();

      // Step 2: Upload images if any (filter out blob URLs)
      // New uploads are normally persisted immediately by ImageUploadSection.
      // Keep this fallback for media added by older clients or deferred uploads.
      const validImages = images.filter(img => !img.id && (img.file || !img.url.startsWith('blob:')));

      if (validImages.length < images.length) {
        console.warn('[CreatePost] Skipped blob URLs - upload not complete');
      }

      if (validImages.length > 0) {
        const uploadedImages = [];
        for (const image of validImages) {
          const uploaded = image.file
            ? await blogAPI.admin.uploadBlogMedia(blog.id, image.file, updated.id, 'post-media')
            : null;
          uploadedImages.push({
            url: uploaded?.data.url ?? image.url,
            type: image.type,
            caption: image.caption,
            sort_order: image.sort_order,
            drive_file_id: uploaded?.data.fileId ?? image.drive_file_id ?? undefined,
            drive_owner_id: uploaded ? Number(uploaded.data.ownerId) : image.drive_owner_id ?? undefined,
          });
        }
        await blogAPI.admin.addPostImagesBatch(updated.id, uploadedImages);
      }

      // Publishing is a separate explicit operation from editing the draft.
      const published = await blogAPI.admin.publishPost(updated.id);
      toast.success('Đã xuất bản bài viết!');
      navigate(`/posts/${published.data.slug}`);
    } catch (error: any) {
      toast.error(error?.message || 'Không thể tạo bài viết');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (blog) {
      navigate(`/blogs/${blog.slug}`);
    } else {
      navigate(-1);
    }
  };

  if (!blog) {
    return <Loading text="Đang tải blog..." description="Vui lòng đợi trong giây lát" />;
  }

  return (
    <div className="flex-1 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Quay lại
          </Button>
          <h1 className="text-2xl md:text-3xl font-bold">Tạo Bài Viết Mới</h1>
          <p className="text-gray-600 mt-1">Blog: {blog.title}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title" className="text-base">
                Tiêu đề <span className="text-red-500">*</span>
              </Label>
              <AutoExpandTextarea
                id="title"
                placeholder="Nhập tiêu đề bài viết..."
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                disabled={loading}
                className="text-base md:text-lg font-medium"
                minRows={1}
                maxRows={3}
                autoFocus
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category" className="text-base">
                Danh mục
              </Label>
              <Select
                value={formData.categoryId}
                onValueChange={(value) =>
                  setFormData({ ...formData, categoryId: value })
                }
                disabled={loading}
              >
                <SelectTrigger className="text-base">
                  <SelectValue placeholder="Chọn danh mục (tùy chọn)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Không có danh mục</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id.toString()}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Content */}
            <div className="space-y-2">
              <Label htmlFor="content" className="text-base">
                Nội dung <span className="text-red-500">*</span>
              </Label>
              <AutoExpandTextarea
                id="content"
                placeholder="Bạn đang nghĩ gì?"
                value={formData.content}
                onChange={(e) =>
                  setFormData({ ...formData, content: e.target.value })
                }
                disabled={loading}
                minRows={3}
                maxRows={15}
                className="text-sm md:text-base"
              />
            </div>

            {/* Images */}
            <ImageUploadSection
              blogId={blog.id}
              images={images}
              onImagesChange={setImages}
              postId={draftPostId ?? undefined}
              coverImageId={coverImageId}
              onCoverChange={setCoverImageId}
              disabled={loading || !draftReady || !draftPostId}
              maxImages={50}
              maxVideos={3}
            />
          </div>

          {/* Action Buttons - Sticky on mobile */}
          <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 -mx-4 md:mx-0 md:relative md:bg-transparent md:border-0 md:p-0">
            <div className="flex gap-3 justify-end max-w-4xl mx-auto">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
                className="flex-1 md:flex-initial"
              >
                Hủy
              </Button>
              <Button
                type="submit"
                disabled={loading || !formData.title.trim() || !formData.content.trim()}
                className="flex-1 md:flex-initial"
              >
                {loading ? (
                  <>
                    <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] mr-2" />
                    Đang tạo...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Đăng Bài Viết
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
